import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Flag, CheckCircle } from "lucide-react";
import { useReportEvent, REPORT_REASONS, type ReportReason } from "../lib/api/useEventReport";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
}

export function ReportEventModal({ isOpen, onClose, slug }: Props) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const report = useReportEvent(slug);

  const handleClose = () => {
    onClose();
    // Reset after close animation
    setTimeout(() => {
      setReason(null);
      setDescription("");
      setSubmitted(false);
    }, 300);
  };

  const handleSubmit = () => {
    if (!reason) return;
    report.mutate(
      { reason, description: description.trim() || undefined },
      { onSuccess: () => setSubmitted(true) },
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <div className="fixed inset-x-0 bottom-0 z-[61] flex justify-center px-0 sm:px-4 sm:items-end sm:pb-4">
            <motion.div
              className="w-full sm:max-w-md bg-white dark:bg-neutral-900 rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl"
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 34 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag indicator */}
              <div className="flex justify-center pt-3 pb-1 sm:hidden">
                <div className="w-10 h-1 rounded-full bg-neutral-300 dark:bg-neutral-700" />
              </div>

              <div className="px-5 pt-3 pb-2 flex items-center gap-3">
                <Flag className="w-5 h-5 text-rose-500 shrink-0" />
                <h2 className="text-base font-semibold flex-1">Report event</h2>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-1.5 rounded-full text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-5 pb-safe-or-5">
                {submitted ? (
                  <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <CheckCircle className="w-12 h-12 text-emerald-500" />
                    <p className="font-semibold">Thanks for letting us know</p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      We'll review this event and take appropriate action.
                    </p>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="mt-2 btn btn-secondary w-full"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
                      Why are you reporting this event?
                    </p>

                    <div className="space-y-2 mb-4">
                      {REPORT_REASONS.map((r) => (
                        <button
                          key={r.value}
                          type="button"
                          onClick={() => setReason(r.value)}
                          className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-left text-sm transition-colors ${
                            reason === r.value
                              ? "bg-primary-500/10 border border-primary-500/30 text-primary-600 dark:text-primary-400 font-medium"
                              : "bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700"
                          }`}
                        >
                          <span
                            className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                              reason === r.value
                                ? "border-primary-500 bg-primary-500"
                                : "border-neutral-400 dark:border-neutral-600"
                            }`}
                          >
                            {reason === r.value && (
                              <span className="w-1.5 h-1.5 rounded-full bg-white" />
                            )}
                          </span>
                          {r.label}
                        </button>
                      ))}
                    </div>

                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Additional details (optional)"
                      rows={3}
                      maxLength={1000}
                      className="textarea textarea-bordered w-full resize-none text-sm mb-4"
                    />

                    <button
                      type="button"
                      disabled={!reason || report.isPending}
                      onClick={handleSubmit}
                      className="btn btn-primary w-full mb-4"
                    >
                      {report.isPending ? "Submitting…" : "Submit report"}
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
